/**
 * Simple event system for decoupling components/contexts
 * This avoids circular dependencies between contexts
 */

export interface EventEmitter<T = any> {
    emit: (data?: T) => void;
    subscribe: (callback: (data?: T) => void) => () => void;
  }
  
  /**
   * Creates a named event that can be emitted and subscribed to
   * 
   * @param name - The name of the event (used for DOM event name)
   * @returns An event emitter object with emit and subscribe methods
   */
  export const createEvent = <T = any>(name: string): EventEmitter<T> => {
    return {
      emit: (data?: T) => {
        const event = new CustomEvent(name, { detail: data });
        window.dispatchEvent(event);
      },
      subscribe: (callback: (data?: T) => void) => {
        const handler = (e: CustomEvent) => callback(e.detail);
        window.addEventListener(name, handler as EventListener);
        return () => window.removeEventListener(name, handler as EventListener);
      }
    };
  };
  
  /**
   * Auth-related events for communication between contexts
   */
  export const AuthEvents = {
    /**
     * Emitted immediately before logout process starts
     * Components should use this to clean up their state
     */
    BEFORE_LOGOUT: createEvent('auth:before_logout'),
    
    /**
     * Emitted after logout process completes
     * Components can use this to initialize anonymous state
     */
    AFTER_LOGOUT: createEvent('auth:after_logout'),
    
    /**
     * Emitted after successful login
     * Components can use this to load user-specific data
     */
    LOGIN_SUCCESS: createEvent('auth:login_success'),
    
    /**
     * Emitted when login fails
     * Components can use this to handle login failures
     */
    LOGIN_FAILED: createEvent('auth:login_failed'),
  };
  
  /**
   * Chat-related events for communication between contexts
   */
  export const ChatEvents = {
    /**
     * Emitted when a new conversation is created
     */
    CONVERSATION_CREATED: createEvent('chat:conversation_created'),
    
    /**
     * Emitted when a conversation is deleted
     */
    CONVERSATION_DELETED: createEvent('chat:conversation_deleted'),
    
    /**
     * Emitted when a conversation is updated
     */
    CONVERSATION_UPDATED: createEvent('chat:conversation_updated'),
    
    /**
     * Emitted when a chat error occurs
     */
    CHAT_ERROR: createEvent('chat:error'),
  };